import { createContext, useContext, useEffect, useRef } from 'react'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithGlobalState } from '@highlight/ipc/jotai'

/**
 * Sidepanel visibility state
 */
export type SidepanelState = 'hidden' | 'peeking' | 'open'

export const sidepanelStateAtom = atomWithGlobalState<SidepanelState>('sidepanel.state', 'hidden')

export const useSidepanelState = () => {
  return useAtomValue(sidepanelStateAtom)
}

/**
 * True under the sidepanel's own composer. The chat input is shared with the Assistant overlay and
 * the side-panel window, and only the sidepanel's copy should react to the sidepanel hiding.
 */
export const InSidepanelContext = createContext(false)

/**
 * Runs `onHide` as the sidepanel goes hidden, when called inside it. Popups portal into the
 * overlay, which stays on screen after the sidepanel slides away, so anything open in the sidepanel
 * has to be closed by hand.
 */
export const useOnSidepanelHidden = (onHide: () => void) => {
  const inSidepanel = useContext(InSidepanelContext)
  const state = useSidepanelState()
  const previous = useRef(state)
  useEffect(() => {
    if (inSidepanel && state === 'hidden' && previous.current !== 'hidden') {
      onHide()
    }
    previous.current = state
  }, [inSidepanel, state, onHide])
}

export const useSetSidepanelState = () => {
  return useSetAtom(sidepanelStateAtom)
}

export const useSidepanel = () => {
  return useAtom(sidepanelStateAtom)
}

/**
 * What initiated the current peek. The peek chevron follows the cursor's edge
 * proximity for an 'edge' peek (cursor is at the right edge, so it can retract
 * as you pull away) but stays pinned in for a 'mini' peek (cursor is on the
 * Mini, far from the edge, where proximity would never bring it on screen).
 * Only meaningful while peeking; left as-is otherwise.
 */
export type PeekSource = 'edge' | 'mini'

const sidepanelPeekSourceAtom = atom<PeekSource | null>(null)

export const useSidepanelPeekSource = () => useAtomValue(sidepanelPeekSourceAtom)
export const useSetSidepanelPeekSource = () => useSetAtom(sidepanelPeekSourceAtom)

/**
 * Gateway connection status - synced from main process via GlobalState
 */
export type GatewayStatus = 'disconnected' | 'connected' | 'connecting'

const gatewayStatusAtom = atomWithGlobalState<GatewayStatus>('gateway.status', 'disconnected')
const isGatewayConnectedAtom = atom((get) => get(gatewayStatusAtom) === 'connected')

export const useGatewayStatus = () => useAtomValue(gatewayStatusAtom)
export const useIsGatewayConnected = () => useAtomValue(isGatewayConnectedAtom)

/**
 * Focus intent when sidepanel opens — 'chat' focuses chat input (Mini),
 * 'reminders' focuses the reminders section, null means no auto-focus.
 */
export const sidepanelOpenFocusIntentAtom = atom<'chat' | 'reminders' | null>(null)
